import { Suspense, lazy, useEffect } from 'react'
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
  useRouteError,
  useRoutes,
} from 'react-router-dom'
import { ErrorBoundary } from 'components/errorBoundary'
import { useSelector } from 'react-redux'
import { RootState } from 'config/user/store'
import { Routes } from 'config/routesEndpoints'
import { NoDataBgIcon, NoDataIcon } from 'components/icons'
import { useTranslation } from 'react-i18next'

const AuthLayout = lazy(() => import('layout/AuthLayout'))
const MainLayout = lazy(() => import('layout/MainLayout'))

// Lazy-loaded route groups
const ConfiguratorRoutes = lazy(
  () => import('pages/privatePages/Configurator/routes')
)
const SettingsRoutes = lazy(() => import('pages/privatePages/Settings/routes'))
const NetworkRoutes = lazy(
  () => import('pages/privatePages/NetworkManagement/routes')
)
const UserRoutes = lazy(
  () => import('pages/privatePages/UserManagement/routes')
)
const TemplateRoutes = lazy(
  () => import('pages/privatePages/TemplateManagement/routes')
)
const UserIms = lazy(() => import('pages/privatePages/Ims/routes'))
const UserNotifications = lazy(
  () => import('pages/privatePages/NotificationManagement/routes')
)
const AuthenticationRoutes = lazy(
  () => import('pages/privatePages/Authentication/routes')
)
const InspectionRoutes = lazy(
  () => import('pages/privatePages/Inspections/routes')
)
const LoginPage = lazy(() => import('pages/publicPages/index'))
const TemplateBuilder = lazy(
  () => import('pages/privatePages/TemplateManagement/Template/Builder')
)
const CoachMode = lazy(() => import('pages/privatePages/CoachMode'))

export const Loader = () => {
  const { t } = useTranslation()

  return <div>{t('loading')}</div>
}
export const SuspenseOutlet = () => {
  return (
    <ErrorBoundary>
      <Suspense fallback={<Loader />}>
        <Outlet />
      </Suspense>
    </ErrorBoundary>
  )
}
export const Root = () => (
  <div id="detail">
    <SuspenseOutlet />
  </div>
)

export const ErrorPage = () => {
  const { t } = useTranslation()
  const error = useRouteError()
  const statusText = (error as { statusText?: string })?.statusText
  const message = (error as { message?: string })?.message

  return (
    <div id="error-page">
      <h1>{t('Oops')}</h1>
      <p>{t('Oops_desc')} </p>
      <p>
        <i>{statusText || message}</i>
      </p>
    </div>
  )
}

export const PageNotFound = () => {
  const { t } = useTranslation()
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <NoDataIcon />
        <h2 style={{ paddingTop: 10 }}>{t('page_not_found')}</h2>
        <h3 style={{ fontWeight: 'normal', color: '#475467' }}>
          {t('page_not_found_desc')}
        </h3>
      </div>
      <NoDataBgIcon />
    </div>
  )
}

const supportedLangs = ['en', 'it', 'es', 'br', 'de', 'fr', 'zu', 'zh']
const defaultLang = 'en'

function LangInjector() {
  const navigate = useNavigate()
  const { lang } = useParams<{ lang?: string }>()
  const location = useLocation()
  useEffect(() => {
    const segments = location.pathname.split('/').filter(Boolean)
    const rest = segments.slice(lang ? 1 : 0).join('/')
    if (!lang) {
      navigate(`/${defaultLang}/${rest}`, { replace: true })
    } else if (!supportedLangs.includes(lang)) {
      navigate(`/${defaultLang}/${rest}`, { replace: true })
    }
  }, [lang, location.pathname, navigate])

  return (
    <Suspense fallback={<Loader />}>
      <AppRoutes
        lang={lang && supportedLangs.includes(lang) ? lang : defaultLang}
      />
    </Suspense>
  )
}

function getRoutes(isAuthenticated: boolean, lang: string, permission?: any) {
  if (!isAuthenticated) {
    return [
      {
        path: '',
        element: <MainLayout />,
        children: [{ path: '', element: <LoginPage /> }],
      },
      {
        path: `${Routes.magicLink}/:id`,
        element: <MainLayout lang={false} />,
        children: [
          {
            index: true,
            element: <CoachMode />,
          },
        ],
      },
      { path: '*', element: <PageNotFound /> },
    ]
  }
  return [
    {
      path: `${Routes.template}/${Routes.builder}`,
      element: <MainLayout />,
      children: [{ index: true, element: <TemplateBuilder /> }],
    },
    {
      path: `${Routes.coachMode}`,
      element: <MainLayout lang={false} />,
      children: [{ index: true, element: <CoachMode /> }],
    },
    {
      path: '',
      element: <AuthLayout />,
      children: [
        {
          path: Routes.rootUrl,
          element: <Navigate to={`/${lang}/${Routes.configurator}`} replace />,
        },
        { path: `${Routes.configurator}/*`, element: <ConfiguratorRoutes /> },
        { path: `${Routes.settings}/*`, element: <SettingsRoutes /> },
        { path: `${Routes.network}/*`, element: <NetworkRoutes /> },
        { path: `${Routes.template}/*`, element: <TemplateRoutes /> },
        { path: `${Routes.user}/*`, element: <UserRoutes /> },
        { path: `${Routes.ims}/*`, element: <UserIms /> },
        { path: `${Routes.notifications}/*`, element: <UserNotifications /> },
        {
          path: `${Routes.authentication}/*`,
          element: <AuthenticationRoutes />,
        },
        permission?.allow_inspeq
          ? { path: `${Routes.inspections}/*`, element: <InspectionRoutes /> }
          : { path: `${Routes.inspections}/*`, element: <PageNotFound /> },
        { path: '*', element: <PageNotFound /> },
      ],
    },
  ]
}

interface AppRoutesProps {
  lang: string
}
const AppRoutes = ({ lang }: AppRoutesProps) => {
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  )

  const user = useSelector((state: RootState) => state.auth.user)
  const { permission } = user || {}

  const navigate = useNavigate()
  const routes = getRoutes(isAuthenticated, lang, permission)
  useEffect(() => {
    const isMagicLink = window.location.pathname.includes('/magic-link/')
    if (!isAuthenticated && !isMagicLink) {
      localStorage.setItem('dynamicRoute', window.location.pathname)
      navigate(`/${lang}`, { replace: true })
    }
  }, [isAuthenticated, navigate, lang])
  return useRoutes(routes)
}

const router = createBrowserRouter([
  { path: '/', element: <Navigate to={`/${defaultLang}`} replace /> },
  {
    path: '/:lang/*',
    element: <LangInjector />,
    errorElement: <ErrorPage />,
  },
  { path: '*', element: <Navigate to={`/${defaultLang}`} replace /> },
])

export { router }
